import React, {useState, useEffect} from 'react';
import moment from "moment";
import { getApisErrors, getDateError, getDocumentTypeError, getDropdownError, getGenderError, getNameError } from "../../utils/validationUtils";
import ThemableSvgIcon from "../../components/theme/ThemableSvgIcon";
import TextInputWithLabel from "../../components/TextInputWithLabel";
import RadioButton from "../../components/RadioButton";
import SelectDropdown from "../../components/SelectDropdown";
import {isValidDate, potentialdmYtoDMY} from "../../utils/dateTimeUtils";
import BackLink from "../../components/BackLink";
import {postToHub} from "../../actions/networkActions";
import Checkbox from "../../components/Checkbox";
import { Trans, useLingui } from '@lingui/react/macro';
import { getIsoCountries, getIsoNationalities } from '../../utils/themeUtils';
import { navigate } from '../../utils/navigationUtils';
import { Navigate } from 'react-router';
import useGet from "../../hooks/useGet";


/*
  The passenger data normally arrives in the location state, handed over by the passport
  dashboard so we do not refetch what it has already loaded. That state is missing whenever
  the url is opened directly - a bookmark, a new tab, or typing it in the address bar - so
  fall back to loading it ourselves in that case.
*/
function PassportInfoForm(props) {
    const routeState = props.location.state;

    if (routeState?.passenger) {
        return (
            <PassportForm
                {...props}
                passenger={routeState.passenger}
                passportValidityRange={routeState.passportValidityRange}
                passportOnlyAllowed={routeState.passportOnlyAllowed}
            />
        );
    }

    return (
        <PassportFormFromApi {...props} />
    );
}

function PassportFormFromApi(props) {
    // useGet returns false until the request resolves, which separates loading from an empty list
    const apiData = useGet('passengers/api/v2');
    const {t} = useLingui();

    if (apiData === false) {
        return (
            <div className="container mx-auto mt-12">
                <span translate="no"><Trans id="loading">loading</Trans>...</span>
            </div>
        );
    }

    const {passengers = [], passportValidityRange, passportOnlyAllowed} = apiData;
    const passenger = passengers.find(candidate => candidate.passengerId === props.params.passengerId);

    if (!passenger) {
        return (
            <Navigate to={t({id: "/precruise/passportDashboard"})} replace />
        );
    }

    return (
        <PassportForm
            {...props}
            passenger={passenger}
            passportValidityRange={passportValidityRange}
            passportOnlyAllowed={passportOnlyAllowed}
        />
    );
}

function PassportForm(props) {
    const passenger = props.passenger;
    const passportValidityRange = props.passportValidityRange;
    const passportOnlyAllowed = props.passportOnlyAllowed;

    const minMaxDates = {
        issueDateMin:passportValidityRange?.minimumPassportIssueDate,
        today: moment().format("DD/MM/YYYY"),
        expiryDateMin:passportValidityRange?.minimumPassportExpiryDate,
    }

    const initialFormState = {
        ...passenger.advancedPassengerInformation,
        ...passportOnlyAllowed ? {documentType: "Passport"} : {}, // if true, documentType should be "Passport"
        terms: false 
    };

    const [formState, setFormState] = useState(initialFormState);
    const [errors, setErrors] = useState({});
    const [submitEnabled, setSubmitEnabled] = useState(false);
    const [submitInProgress, setSubmitInProgress] = useState(false);
    const [submitErrorMessage, setSubmitErrorMessage] = useState("");

    const {t, i18n} = useLingui();

    useEffect(() => {
        checkIfFormValid(formState);
    }, []);


    function checkIfFormValid(formValues) {
        const isValid = Object.keys(getApisErrors(formValues, false, minMaxDates)).length === 0;
        setSubmitEnabled(isValid);

        if(submitErrorMessage) {
            setSubmitErrorMessage("");
        }
    }


    function updateFormValue(fieldName, value) {
        const passengerFormValues = {...formState};
        passengerFormValues[fieldName] = value;
        setFormState(passengerFormValues);

        checkIfFormValid(passengerFormValues);
    }

    async function submit() {

        if(passportOnlyAllowed){
            formState.documentType = "Passport";
        }

        // check for errors
        const validationErrors = getApisErrors(formState, true, minMaxDates);
        if(Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }


        if(submitInProgress) {
            return;
        }

        //craft the body with single element in array
        const body = {
            passengers: [
                {
                    ...formState,
                    passengerId: passenger.passengerId,
                    dateOfBirth: potentialdmYtoDMY(formState.dateOfBirth),
                    documentIssuedDate: potentialdmYtoDMY(formState.documentIssuedDate),
                    documentExpiryDate: potentialdmYtoDMY(formState.documentExpiryDate),
                }
            ]
        };

        //do the submit
        setSubmitInProgress(true);
        const result = await postToHub("passengers/api/submitApi", body);
        setSubmitInProgress(false);

        // success
        if(result === "") {
            navigate(t({id: "/precruise/passportDashboard"}));
            return;
        }

        // error handling
        if(result?.error) {
            const errorCode = result?.error?.response?.status;
            if(errorCode === 400 && passenger.passengerId === result.error.response.data?.passengerId) {
                const fieldNames = {
                    firstName: t({id: "first_name"}),
                    lastName: t({id: "sur_name"}),
                    gender: t({id: "gender"}),
                    dateOfBirth: t({id: "date_of_birth"}),
                    documentType: t({id: "travel_document_type"}),
                    documentNumber: t({id: "travel_document_number"}),
                    documentIssuedDate: t({id: "issue_date"}),
                    documentExpiryDate: t({id: "expiry_date"}),
                    documentIssuanceCountry: t({id: "issuing_country"}),
                    nationality: t({id: "nationality_region"})
                };

                const fieldName = result.error.response.data.errorField;
                const fieldLabel = (fieldNames[fieldName])?.toLowerCase() || fieldName;

                setError(fieldName, t({id: "please_make_sure_this_field_is_valid"}));
                setSubmitErrorMessage(i18n._({
                    id: "correct_the_field_and_resubmit_form",
                    message:'Please correct the field {fieldLabel} and resubmit the form',
                    values: {fieldLabel}
                }));
            }
            else {
                setSubmitErrorMessage(t({id: "error_submitting_form_try_again"}))
            }
        }
        else {
            setSubmitErrorMessage(t({id: "unexpected_error_happened"}));
        }
    }

    function setError(fieldName, error) {
        const updatedErrors = {...errors};

        if(error) {
            updatedErrors[fieldName] = error;
        }
        else {
            delete updatedErrors[fieldName];
        }

        setErrors(updatedErrors);
    }


    return (
        <>
            <div className="container mx-auto my-4">
                <BackLink to="/" text={t({id: "back_to_passport_information_dashboard"})}/>
            </div>

            <div className="bg-grey-lighter py-4 mb-8">
                <div className="container mx-auto">
                    <div className="flex">
                        <ThemableSvgIcon name="info-inverted" className="shrink-0 w-6 h-6 text-primary-dark"/>
                        <div className="ml-2 text-left leading-normal">
                            <Trans id="passport_info_text">
                                You <strong>must</strong> travel using the same document you provide here.
                                <strong> If you supply incorrect information you may be denied boarding.</strong>
                                <div className="mt-2">
                                    <strong>
                                        Once you have checked in, you will no longer be able to edit these.
                                    </strong>
                                </div>
                            </Trans>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto mb-8 xl:mb-12">
                    <PassengerApisForm
                        passenger={passenger}
                        formValues={formState}
                        onUpdate={updateFormValue}
                        errors={errors}
                        onValidate={setError}
                        minMaxDates={minMaxDates}
                        passportOnlyAllowed={passportOnlyAllowed}
                    />

                <div className="mt-6">
                    <button className={`w-mobile action-button ${submitEnabled && !submitInProgress ? "action-button-enabled" : "action-button-disabled"}`} onClick={submit}>
                        <Trans id="submit_and_return_to_booking">Submit and return to booking</Trans>
                    </button>
                </div>
                <ErrorMessage message={submitErrorMessage} />
            </div>
        </>
    );
}

export default PassportInfoForm;

function PassengerApisForm(props) {
    const passenger = props.passenger;
    const passportOnlyAllowed = props.passportOnlyAllowed;

    const {t, i18n} = useLingui();

    function handleChange(fieldName, value) {
        props.onUpdate(fieldName, value);

        if(props.errors[fieldName]) {
            const error = getErrorForField(fieldName, value)
            props.onValidate(fieldName, error);
        }
    }

    function handleBlur(fieldName, value) {
        const error = getErrorForField(fieldName, value)
        props.onValidate(fieldName, error);
    }

    function getErrorForField(fieldName, value) {
        let error = "";
        switch (fieldName) {
            case "middleName":
                error = getNameError(value, false);
                break;

            case "firstName":
            case "lastName":
                error = getNameError(value, true);
                break;

            case "gender":
                error = getGenderError(value);
                break;

            case "dateOfBirth":
            case "documentExpiryDate":
            case "documentIssuedDate":
                error = getDateError(value);
                break;

            case "documentType":
                error = getDocumentTypeError(value);
                break

            case "documentIssuanceCountry":
            case "nationality":
                error = getDropdownError(value);
                break;
        }
        return error;
    }

    const issueDateValidationMessage = props.minMaxDates.issueDateMin
        ? i18n._({
                id: "passport_issue_date_must_between_text",
                message: `Due to new guidelines your passport issue date must be between {minDate} and {today}`,
                values: {
                    minDate: props.minMaxDates.issueDateMin,
                    today: props.minMaxDates.today
                }
            })
        : i18n._({
                id: "passport_issue_date_must_before_text",
                message: `Your passport issue date must be before {today}`,
                values: {
                    today: props.minMaxDates.today
                }
            });     

    const expiryDateValidationMessage = props.minMaxDates.expiryDateMin
        ? i18n._({
                id: "passport_expiry_date_must_after_text",
                message: `Due to new guidelines your passport expiry date must be after {minDate}`,
                values: {
                    minDate: props.minMaxDates.expiryDateMin
                }
            })
        : i18n._({
                id: "passport_expiry_date_must_after_today_text",
                message: `Your passport expiry date must be after {today}`,
                values: {
                    today: props.minMaxDates.today
                }
            });

    return (
        <div className="border mt-6">
            <div className="border-b p-4">
                <div className="flex items-center text-primary-dark text-3xl font-bold">
                    <ThemableSvgIcon name="adult" className="w-8 h-8"/>
                    <div className="ml-2">
                        {passenger.title} {passenger.firstName} {passenger.lastName}
                    </div>
                </div>
            </div>

            <div className="p-4 bg-grey-lightest">
                <div className="text-grey-darker leading-normal">
                    <Trans id="passport_entry_guideline">
                        Please ensure that the information entered exactly matches your travel document.
                    </Trans>
                </div>

                <div className="w-full md:w-3/4 xl:w-1/2">

                    <div className="sm:flex">
                        <div className="sm:grow sm:mr-2">
                            <TextInputWithLabel
                                classNames="w-full mt-4"
                                label={t({id: "first_name"})}
                                value={props.formValues.firstName || ""}
                                _onChange={value => {handleChange("firstName", value)}}
                                onBlur={e => handleBlur("firstName", e.target.value)}
                                hasError={props.errors.firstName}
                            />
                            {props.errors.firstName &&
                            <div className="sm:hidden mt-1 errors">
                                {props.errors.firstName}
                            </div>
                            }
                        </div>

                        <div className="sm:grow sm:mr-2">
                            <TextInputWithLabel
                                classNames="w-full mt-4"
                                label={t({id: "middle_name_s"})}
                                value={props.formValues.middleName || ""}
                                _onChange={value => {handleChange("middleName", value)}}
                                onBlur={e => handleBlur("middleName", e.target.value)}
                                hasError={props.errors.middleName}
                            />
                            {props.errors.middleName &&
                            <div className="sm:hidden mt-1 errors">
                                {props.errors.middleName}
                            </div>
                            }
                        </div>

                        <div className="sm:grow">
                            <TextInputWithLabel
                                classNames="w-full mt-4"
                                label={t({id: "sur_name"})}
                                value={props.formValues.lastName || ""}
                                _onChange={value => {handleChange("lastName", value)}}
                                onBlur={e => handleBlur("lastName", e.target.value)}
                                hasError={props.errors.lastName}
                            />
                            {props.errors.lastName &&
                            <div className="sm:hidden mt-1 errors">
                                {props.errors.lastName}
                            </div>
                            }
                        </div>
                    </div>

                    {props.errors.firstName &&
                    <div className="hidden sm:block mt-2 errors">
                        {props.errors.firstName}
                    </div>
                    }

                    {props.errors.middleName &&
                    <div className="hidden sm:block mt-2 errors">
                        {props.errors.middleName}
                    </div>
                    }

                    {props.errors.lastName &&
                    <div className="hidden sm:block mt-2 errors">
                        {props.errors.lastName}
                    </div>
                    }

                    <div className="mt-4">
                        <div className="input-label">
                            {t({id: "gender"})}
                        </div>
                        <div className="flex mt-1">
                            <div>
                                <RadioButton
                                    label={t({id: "male"})}
                                    _onChange={() => handleChange("gender", "Male")}
                                    isSelected={props.formValues.gender === "Male"}
                                />
                            </div>
                            <div className="ml-4">
                                <RadioButton
                                    label={t({id: "female"})}
                                    _onChange={() => handleChange("gender", "Female")}
                                    isSelected={props.formValues.gender === "Female"}
                                />
                            </div>
                        </div>
                        {props.errors.gender &&
                        <div className="mt-2 errors">
                            {props.errors.gender}
                        </div>
                        }
                    </div>

                    <DateInput
                        label={t({id: "date_of_birth_and_format"})}
                        value={props.formValues.dateOfBirth}
                        onChange={(value) => handleChange("dateOfBirth", value)}
                        error={props.errors.dateOfBirth}
                    />

                    {!passportOnlyAllowed && (
                        <div className="mt-4">
                            <SelectDropdown
                                selectId="document-type-dropdown"
                                labelClasses="block input-label mb-1"
                                label={t({id: "travel_document"})}
                                error={props.errors.documentType}
                                options={[
                                    {value: "Select document type", label: t({id: "select_document_type"})},
                                    {value: "Passport", label: t({id: "passport"})},
                                    {value: "Identity Card", label: t({id: "identity_card"})},
                                ]}
                                handleChange={e => handleChange("documentType", e.target.value)}
                                handleBlur={e => handleBlur("documentType", e.target.value)}
                                value={props.formValues.documentType}
                                showPlaceHolder={false}
                            />
                            <ErrorMessage message={props.errors.documentType} />
                        </div>
                    )}

                    <TextInputWithLabel
                        classNames="w-full mt-4"
                        label={passportOnlyAllowed ? t({id: "passport_number"}) : t({id: "travel_document_number"})}
                        errorMessage={props.errors.documentNumber}
                        value={props.formValues.documentNumber || ""}
                        _onChange={value => handleChange("documentNumber", value)}
                        onBlur={e => handleBlur("documentNumber", e.target.value)}
                    />

                    <DateInput
                        label={t({id: "issue_date"})}
                        subLabel={issueDateValidationMessage}
                        value={props.formValues.documentIssuedDate}
                        onChange={(value) => handleChange("documentIssuedDate", value)}
                        error={props.errors.documentIssuedDate}
                        minDate={props.minMaxDates.issueDateMin}
                        maxDate={props.minMaxDates.today}
                    />

                    <DateInput
                        label={t({id: "expiry_date"})}
                        subLabel={expiryDateValidationMessage}
                        value={props.formValues.documentExpiryDate}
                        onChange={(value) => handleChange("documentExpiryDate", value)}
                        error={props.errors.documentExpiryDate}
                        minDate={props.minMaxDates.expiryDateMin || props.minMaxDates.today}
                    />

                    <div className="mt-4">
                        <SelectDropdown
                            selectId="country-dropdown"
                            labelClasses="block input-label mb-1"
                            label={t({id: "issuing_country"})}
                            error={props.errors.documentIssuanceCountry}
                            options={getIsoCountries()}
                            handleChange={e => handleChange("documentIssuanceCountry", e.target.value)}
                            handleBlur={e => handleBlur("documentIssuanceCountry", e.target.value)}
                            value={props.formValues.documentIssuanceCountry}
                        />
                    </div>


                    <div className="mt-4">
                        <SelectDropdown
                            selectId="nationality-dropdown"
                            labelClasses="block input-label mb-1"
                            label={t({id: "nationality_region"})}
                            error={props.errors.nationality}
                            options={getIsoNationalities()}
                            handleChange={e => handleChange("nationality", e.target.value)}
                            handleBlur={e => handleBlur("nationality", e.target.value)}
                            value={props.formValues.nationality}
                        />
                    </div>
                </div>


                <div className="mt-6">
                    <Checkbox
                        checked={props.formValues.terms}
                        value="tos"
                        handleCheckboxChange={e => handleChange("terms", e.target.checked)}
                    >
                        <Trans id="passport_info_confirmation">I confirm that my passport meets the following requirements:</Trans>
                    </Checkbox>
                    <div>
                        <ol className="list-decimal list-inside mt-2">
                            <Trans id="passport_information_rules">
                                <li>
                                    Less than 10 years old on the day you enter your first port of call (check the ‘date of issue’).
                                </li>
                                <li className="mt-1">
                                    Valid for at least 3 months after the day you plan to leave your final port of call (check the ‘expiry date’). For travel outside of the EU your passport should be valid for 6 months after the date of your return.
                                </li>
                            </Trans>
                        </ol>
                        {props.errors.terms &&
                        <div className="mt-2 errors">
                            {props.errors.terms}
                        </div>
                        }
                    </div>

                </div>
            </div>
        </div>
    )
}

function DateInput(props) {
    const dateParts = props?.value?.split("/") || ["","",""];

    const [errors, setErrors] = useState({day: false, month: false, year: false, date: false});

    const {t} = useLingui();

    function handleChange(field, value) {
        const updatedParts = [...dateParts];
        switch (field) {
            case "day":
                if(value.length < 3) {
                    updatedParts[0] = value;
                }
                break;

            case "month":
                if(value.length < 3) {
                    updatedParts[1] = value;
                }
                break;

            case "year":
                if(value.length < 5) {
                    updatedParts[2] = value;
                }
                break;
        }

        const updatedString = updatedParts.join("/");

        // check if error for this field was set and if so validate again (and potentially remove the error)
        let shouldUpdateErrors = false;
        const newErrors = {...errors};

        if(errors[field]) {
            if(!getError(field, value)) {
                newErrors[field] = false;
                shouldUpdateErrors = true;
            }
        }

        // check if general error is set on date and clear it if problem has been fixed
        if(errors.date) {
            if(isValidDate(updatedString, props.minDate, props.maxDate)) {
                newErrors.date = false;
                shouldUpdateErrors = true;
            }
        }

        if(shouldUpdateErrors) {
            setErrors(newErrors);
        }

        props.onChange(updatedParts.join("/"));
    }

    function handleBlur(field, value) {
        const newErrors = {...errors};
        newErrors[field] = getError(field, value);

        // if all 3 parts are populated, check if date is valid and other relevant date checks
        if(dateParts.every(datePart => datePart)) {
            newErrors.date = isValidDate(props.value, props.minDate, props.maxDate) ? false : t({id: "make_sure_to_enter_valid_date"});
        }

        setErrors(newErrors);
    }

    function getError(field, value) {
        switch (field) {
            case "day":
                const day = parseInt(value);
                return (isNaN(day) || day < 1 || day > 31) ? t({id: "please_enter_a_valid_day"}) : false;

            case "month":
                const month = parseInt(value);
                return (isNaN(month) || month < 1 || month > 12) ?  t({id: "please_enter_a_valid_month"}) : false;

            case "year":
                const year = parseInt(value);
                const currentYear = new Date().getFullYear();

                return (isNaN(year) || (currentYear - year) > 115 || (year - currentYear) > 20) ?
                    t({id: "please_enter_a_valid_year"}) :
                    false;
        }
    }


    return (
        <div className="mt-4">
            <div className="mb-1">
                <div className="input-label">
                    {props.label}
                </div>
                {(props.error || errors.date) && props.subLabel &&
                <div className="text-grey-darker leading-normal">
                    {props.subLabel}
                </div>
                }
            </div>
            <div className="flex">
                <div className="sm:w-1/4 md:w-1/5">
                    <TextInputWithLabel
                        classNames="w-full"
                        type="tel"
                        value={dateParts[0]}
                        _onChange={value => handleChange("day", value)}
                        placeholder={t({id: "dd"})}
                        onBlur={() => handleBlur("day", dateParts[0])}
                        hasError={errors.day || props.error}
                    />
                </div>


                <div className="sm:w-1/4 md:w-1/5">
                    <TextInputWithLabel
                        classNames="ml-2"
                        type="tel"
                        value={dateParts[1]}
                        _onChange={value => handleChange("month", value)}
                        placeholder={t({id: "mm"})}
                        onBlur={() => handleBlur("month", dateParts[1])}
                        hasError={errors.month || props.error}
                    />
                </div>

                <div className="sm:w-1/4 md:w-1/5">
                    <TextInputWithLabel
                        classNames="ml-2"
                        type="tel"
                        value={dateParts[2]}
                        _onChange={value => handleChange("year", value)}
                        placeholder={t({id: "yyyy"})}
                        onBlur={() => handleBlur("year", dateParts[2])}
                        hasError={errors.year || props.error}
                    />
                </div>
            </div>

            <ErrorMessage message={props.error} />
            <ErrorMessage message={errors.day} />
            <ErrorMessage message={errors.month} />
            <ErrorMessage message={errors.year} />
            <ErrorMessage message={errors.date} />
        </div>
    )
}

function ErrorMessage(props) {
    if(!props.message) {
        return null;
    }

    return (
        <div className="mt-2 errors">
            {props.message}
        </div>
    );
}